import { POPUP_TYPES, PopupConfig } from "@smile2impress/popups";
import { UTSLocale } from "@smile2impress/attraction-constants";
import { Popup } from "api/cms-website/cms-popups.types";
import { getLocalizedPathname } from "modules/localize-pathnames/getLocalizedPathname";
import { PictureProps } from "@smile2impress/components/relaunch";

import { UrlsConfig } from "configs/urlsConfig";
import { UTSLocaleToI18nLocale } from "constants/enum";
import getIcon from "utils/getIcon";

/**
 * Pages where the quiz-link popup must never appear. Mirrors the static
 * `excludedPages` the popup used before it became CMS-driven and follows the
 * same in-code approach as PromoCampaign's excluded list (rather than the CMS).
 */
const STATIC_EXCLUDED_URLS = [
    UrlsConfig.UNIVERSAL_FORM,
    UrlsConfig.BOOKING_WIDGET,
    UrlsConfig.THANKS_CONTACT,
    UrlsConfig.STUDENT_BEANS,
    UrlsConfig.REFERRAL,
    UrlsConfig.REFERRAL_FOR_FRIENDS,
    UrlsConfig.QUIZ_RESULTS,
    UrlsConfig.SMAJLA_BOOKING,
    UrlsConfig.SMAJLA_LEADFORM,
    UrlsConfig.SMAJLA_THANKS_CONTACT,
    UrlsConfig.SMAJLA_TYPEFORM,
    UrlsConfig.DRSMILE_BOOKING,
    UrlsConfig.DRSMILE_LEADFORM,
    UrlsConfig.DRSMILE_THANKS_CONTACT,
];

/**
 * Build the localized excluded paths (e.g. `/es/blog`) for a popup's locale,
 * matching the localized `asPath` the popup manager compares against.
 */
const getStaticExcludedPages = (locale: UTSLocale): string[] => {
    const i18nLocale = UTSLocaleToI18nLocale[locale];
    return STATIC_EXCLUDED_URLS.map((url) => {
        const path = getLocalizedPathname(url, i18nLocale, true);
        return path ? `/${i18nLocale}${path}` : undefined;
    }).filter((path): path is string => path !== undefined);
};

let cmsPopups: Popup[] = [];
try {
    // Important: do not replace with `import`! `require` tolerates a missing
    // file (e.g. before the first cms-downloader run) — same as no popups.
    cmsPopups =
        require("../../../../cms-data/cms-popups.json") as unknown as Popup[];
} catch {
    // cms-popups.json not found — no CMS popups (run `yarn fetch-cms-data`).
}

export const mapPopupToConfig = (popup: Popup): PopupConfig => ({
    slug: popup.slug,
    locales: [popup.locale],
    data: {
        type: POPUP_TYPES.QUIZ_LINK,
        content: () => ({
            title: popup.title,
            subtitle: popup.subtitle,
            withImage: popup.withImage,
            cta: {
                text: popup.cta.text,
                link: popup.cta.link,
                iconRight: popup.cta.iconType
                    ? getIcon(popup.cta.iconType)
                    : undefined,
            },
            bgImage: popup.image as PictureProps,
        }),
    },
    pagesExcluded: [
        ...getStaticExcludedPages(popup.locale),
        ...(popup.pagesExcluded ?? []),
    ],
    ...(Array.isArray(popup.pagesIncluded) &&
        popup.pagesIncluded?.length > 0 && {
            pagesIncluded: popup.pagesIncluded,
        }),
});

/**
 * Build runtime PopupConfig entries from the CMS popups JSON. Popups are shown
 * uniformly on any URL that matches their include/exclude rules, blog posts
 * included — there is no separate "for pages" / "for posts" handling.
 */
export const getCmsPopupConfigs = (
    popups: Popup[] = cmsPopups
): PopupConfig[] => popups.map(mapPopupToConfig);
